/**
 * Bridge API Client — Implements INTEGRATION_GUIDE.md Section 8.1
 *
 * SESSION 18 (May 11, 2026): Source-of-truth flipped from WP → OL.
 * All entity reads & writes now hit /api/ol-canonical/{entity}/{org}
 * instead of /api/bridge-proxy/{entity}/{org}. The bridge proxy routes
 * remain mounted for rollback + non-entity calls (config, health,
 * uploads, media). See /app/backend/routes/ol_canonical.py for the
 * canonical adapter layer + background WP sync.
 *
 * Enforces:
 * - Max 6 concurrent requests per tenant
 * - In-memory GET cache with explicit invalidation
 * - Request deduplication (coalescing)
 * - 300ms debounce on search
 * - Exponential backoff on errors (1s, 2s, 4s, 8s, 16s — max 5 attempts)
 * - Circuit breaker: >50% error rate over 1min → pause 30s
 */
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_CONCURRENT = 6;
const MAX_RETRIES = 5;
const BACKOFF_BASE = 1000;
const CIRCUIT_WINDOW_MS = 60000;
const CIRCUIT_PAUSE_MS = 30000;
const CIRCUIT_THRESHOLD = 0.5;

class BridgeApiClient {
  constructor() {
    this.cache = new Map();         // key → {data, ts}
    this.inflight = new Map();      // key → Promise (dedup)
    this.queue = [];                // pending requests
    this.active = 0;                // currently in-flight count
    this.errorLog = [];             // {ts, ok} for circuit breaker
    this.circuitOpen = false;
    this.circuitTimer = null;
  }

  _cacheKey(method, url, params) {
    return `${method}:${url}:${JSON.stringify(params || {})}`;
  }

  // ── Cache Management ──
  getCached(url, params) {
    const key = this._cacheKey('GET', url, params);
    return this.cache.get(key)?.data || null;
  }

  invalidate(pattern) {
    // pattern: string that cache keys must include to be cleared
    for (const [k] of this.cache) {
      if (k.includes(pattern)) this.cache.delete(k);
    }
  }

  invalidateEntity(entityPath) {
    // Clear all caches for an entity type (e.g., "/teams")
    this.invalidate(entityPath);
  }

  clearAll() {
    this.cache.clear();
    this.inflight.clear();
  }

  // ── Circuit Breaker ──
  _recordResult(ok) {
    const now = Date.now();
    this.errorLog.push({ ts: now, ok });
    // Trim old entries
    this.errorLog = this.errorLog.filter(e => now - e.ts < CIRCUIT_WINDOW_MS);
    // Check threshold
    if (this.errorLog.length >= 5) {
      const errRate = this.errorLog.filter(e => !e.ok).length / this.errorLog.length;
      if (errRate > CIRCUIT_THRESHOLD && !this.circuitOpen) {
        console.warn('[BridgeAPI] Circuit breaker OPEN — pausing requests for 30s');
        this.circuitOpen = true;
        this.circuitTimer = setTimeout(() => {
          this.circuitOpen = false;
          console.info('[BridgeAPI] Circuit breaker CLOSED — resuming');
          this._processQueue();
        }, CIRCUIT_PAUSE_MS);
      }
    }
  }

  // ── Request Queue ──
  _processQueue() {
    while (this.queue.length > 0 && this.active < MAX_CONCURRENT && !this.circuitOpen) {
      const { resolve, reject, fn } = this.queue.shift();
      this.active++;
      fn()
        .then(resolve)
        .catch(reject)
        .finally(() => {
          this.active--;
          this._processQueue();
        });
    }
  }

  _enqueue(fn) {
    return new Promise((resolve, reject) => {
      this.queue.push({ resolve, reject, fn });
      this._processQueue();
    });
  }

  // ── Core Request with Retry ──
  async _request(method, url, data, params, retryCount = 0) {
    try {
      const config = { method, url, params };
      if (data && (method === 'post' || method === 'patch' || method === 'put')) {
        config.data = data;
        config.headers = { 'Content-Type': 'application/json' };
      }
      const resp = await axios(config);
      this._recordResult(true);
      return resp.data;
    } catch (err) {
      const status = err.response?.status;
      // Don't retry on 4xx (except 429)
      if (status && status >= 400 && status < 500 && status !== 429) {
        this._recordResult(false);
        throw err;
      }
      // Retry on 429, 5xx, network errors
      if (retryCount < MAX_RETRIES) {
        const delay = BACKOFF_BASE * Math.pow(2, retryCount);
        // Honor Retry-After header
        const retryAfter = err.response?.headers?.['retry-after'];
        const wait = retryAfter ? parseInt(retryAfter) * 1000 : delay;
        await new Promise(r => setTimeout(r, wait));
        return this._request(method, url, data, params, retryCount + 1);
      }
      this._recordResult(false);
      throw err;
    }
  }

  // ── Public API ──

  async get(path, orgId, params = {}) {
    const url = `${API}/api/ol-canonical${path}/${orgId}`;
    const key = this._cacheKey('GET', url, params);

    // Return cached if available
    const cached = this.cache.get(key);
    if (cached) return cached.data;

    // Dedup: if same request is already in-flight, return the same promise
    if (this.inflight.has(key)) return this.inflight.get(key);

    const promise = this._enqueue(() => this._request('get', url, null, params));
    this.inflight.set(key, promise);

    try {
      const result = await promise;
      this.cache.set(key, { data: result, ts: Date.now() });
      return result;
    } finally {
      this.inflight.delete(key);
    }
  }

  async post(path, orgId, data) {
    const url = `${API}/api/ol-canonical${path}/${orgId}`;
    const result = await this._enqueue(() => this._request('post', url, data));
    // Invalidate list cache for this entity
    this.invalidateEntity(path);
    return result;
  }

  async patch(path, orgId, entityId, data) {
    const url = `${API}/api/ol-canonical${path}/${orgId}/${entityId}`;
    const result = await this._enqueue(() => this._request('patch', url, data));
    this.invalidateEntity(path);
    return result;
  }

  async del(path, orgId, entityId, params = {}) {
    const url = `${API}/api/ol-canonical${path}/${orgId}/${entityId}`;
    const result = await this._enqueue(() => this._request('delete', url, null, params));
    this.invalidateEntity(path);
    return result;
  }

  async upload(orgId, data) {
    const url = `${API}/api/bridge-proxy/upload/${orgId}`;
    return this._enqueue(() => this._request('post', url, data));
  }

  async vocabLevels() {
    const { data } = await axios.get(`${API}/api/ol-canonical/vocab-levels`);
    return data;
  }

  async seasonOptions(orgId, sport) {
    const { data } = await axios.get(`${API}/api/ol-canonical/season-options/${orgId}`, { params: { sport: sport || '' } });
    return data;
  }

  // Config endpoints (not rate-limited the same way)
  async getConfig(orgId) {
    const { data } = await axios.get(`${API}/api/bridge-proxy/config/${orgId}`);
    return data;
  }

  async saveConfig(orgId, body) {
    const { data } = await axios.post(`${API}/api/bridge-proxy/config/${orgId}`, body);
    return data;
  }

  async testConfig(orgId) {
    const { data } = await axios.post(`${API}/api/bridge-proxy/config/${orgId}/test`);
    return data;
  }

  async relinkStatus(orgId) {
    const { data } = await axios.get(`${API}/api/bridge-proxy/config/${orgId}/relink-status`);
    return data;
  }

  async deleteConfig(orgId) {
    const { data } = await axios.delete(`${API}/api/bridge-proxy/config/${orgId}`);
    return data;
  }

  // ── Known Sites Registry (multi-site selector) ──
  async listKnownSites(orgId) {
    const { data } = await axios.get(`${API}/api/bridge-proxy/known-sites/${orgId}`);
    return data;
  }

  async addKnownSite(orgId, body) {
    const { data } = await axios.post(`${API}/api/bridge-proxy/known-sites/${orgId}`, body);
    return data;
  }

  async removeKnownSite(orgId, siteUrl) {
    const { data } = await axios.delete(`${API}/api/bridge-proxy/known-sites/${orgId}`, {
      params: { site_url: siteUrl },
    });
    return data;
  }

  async activateKnownSite(orgId, siteUrl) {
    const { data } = await axios.post(`${API}/api/bridge-proxy/known-sites/${orgId}/activate`, {
      site_url: siteUrl,
    });
    // Switching = full cache flush (we're now talking to a different WP)
    this.clearAll();
    return data;
  }

  // ── WP Media Library (MEDIA_CLASSIFICATION_v1) ──
  async mediaRegistry() {
    const { data } = await axios.get(`${API}/api/wp-media/registry`);
    return data;
  }

  async mediaPreviewSlug(category, fields) {
    const { data } = await axios.post(`${API}/api/wp-media/preview-slug`, { category, fields });
    return data;
  }

  async mediaList(orgId, { limit = 100, page = 1, category = null, forceRefresh = false } = {}) {
    const params = { limit, page };
    if (category) params.category = category;
    if (forceRefresh) params.force_refresh = true;
    const { data } = await axios.get(`${API}/api/wp-media/list/${orgId}`, { params });
    return data;
  }

  async mediaDelete(orgId, attachmentId, force = true) {
    const { data } = await axios.delete(`${API}/api/wp-media/detail/${orgId}/${attachmentId}`, {
      params: { force },
    });
    return data;
  }

  async mediaUploadClassified(orgId, file, category, fields, title = null) {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('category', category);
    fd.append('fields', JSON.stringify(fields || {}));
    if (title) fd.append('title', title);
    const { data } = await axios.post(`${API}/api/wp-media/upload-classified/${orgId}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  }
}

// Singleton — shared across all components (per Section 8.1 rule: "one module-level client")
const bridgeApi = new BridgeApiClient();
export default bridgeApi;
